// =============================================================================
// catalog-edition-client.stub.ts — Double déterministe du client d'ÉCRITURE admin
// du BC catalog (A-09 profil + matrice + A-25 compétences + A-10 upload ; S9-D4).
// =============================================================================
// Simule les controllers d'écriture S9 (CatalogEditionController, CatalogSkillController,
// CatalogUploadController) in-memory. L'AUTORITÉ reste l'API (RBAC, inv.1 unicité
// perpétuelle, inv.2 édition réservée DRAFT, poids ∈ [0,1], inv.7 promotion bloquée si
// anomalie bloquante, dry-run SANS création de version) ; ce double rend les tests fidèles
// sans backend ni I/O réseau, sans persistance disque (RULE_REF_006).
//
// Le stub reproduit les gardes serveur (MIROIR) UNIQUEMENT pour rester fidèle : l'UI ne
// consulte JAMAIS cette logique, elle envoie l'intention et affiche l'erreur retournée.
// catalog = référentiel GLOBAL : AUCUNE donnée nominative, AUCUNE PII.

import {
  CATALOG_VERSION_WRONG_STATE,
  CatalogApiError,
  PROFILE_ALREADY_ARCHIVED,
  PROFILE_MERGE_CONFIRMATION_MISMATCH,
  PROFILE_MERGE_OPERAND_ARCHIVED,
  PROFILE_MERGE_SAME_PROFILE,
  PROFILE_NOT_ARCHIVED,
  SKILL_ARCHIVED_NOT_EDITABLE,
  SKILL_NOT_ARCHIVED,
  type CatalogAdminEditionClient,
  type CatalogVersionDto,
  type ConfirmUploadResult,
  type CorrelationEdgeView,
  type CreateSkillInputDto,
  type EditCorrelationEdgeInputDto,
  type EditProfileInputDto,
  type ProfileCampaignUsageEntry,
  type ProfileView,
  type SkillView,
  type UpdateSkillInputDto,
  type UploadDryRunPreview,
} from '@/api/catalog-client';

/** Seed d'usage campagne d'un profil (avertissement d'archivage §2.4) : indexe une Entry par profileCode. */
export interface ProfileCampaignUsageSeed {
  readonly profileCode: string;
  readonly entry: ProfileCampaignUsageEntry;
}

/**
 * Seed d'usage campagne d'une COMPÉTENCE (avertissement d'archivage R15-CAT-03, DEC-27) : indexe une Entry
 * par skillCode. Représente la réponse API DÉJÀ résolue « proposable » + filtrée « en cours » par le carrier
 * back — le stub la restitue telle quelle (aucune résolution famille→profil côté front).
 */
export interface SkillCampaignUsageSeed {
  readonly skillCode: string;
  readonly entry: ProfileCampaignUsageEntry;
}

/** Identifiant de la version DRAFT du jeu de démonstration (édition autorisée). */
export const DEMO_DRAFT_VERSION_ID = 'cat-v3-draft';

/**
 * MIROIR déterministe de `@updatedAt` : fait avancer un horodatage ISO de +1s (monotone). Permet aux UT
 * de prouver que la « date de dernière modification » BOUGE après une édition (S210-AUDIT-MODIF #24).
 */
function bumpIso(iso: string): string {
  return new Date(Date.parse(iso) + 1000).toISOString();
}

/** Jeu de Profiles de démonstration (version DRAFT). */
function demoProfiles(): ProfileView[] {
  return [
    {
      profileCode: 'PROF-DEV-001',
      catalogVersionId: DEMO_DRAFT_VERSION_ID,
      libelle: 'Développeur back-end',
      famille: 'Études et développement',
      sousFamille: 'Back-end',
      description: 'Conception et développement de services serveur.',
      availableLevels: ['Maîtrisant', 'Expert'],
      state: 'Active',
      contextes: 'Équipes produit, delivery en cycles courts.',
      environnements: 'Node.js, PostgreSQL, GCP.',
      missions: '<p>Concevoir et opérer des services serveur robustes.</p>',
      competencesByLevel: {
        Maîtrisant: ['Node.js', 'SQL'],
        Expert: ['Architecture distribuée', 'Sécurité applicative'],
      },
    },
    {
      profileCode: 'PROF-DATA-002',
      catalogVersionId: DEMO_DRAFT_VERSION_ID,
      libelle: 'Ingénieur data',
      famille: 'Data',
      sousFamille: 'Data engineering',
      description: 'Pipelines de données et plateformes analytiques.',
      availableLevels: ['Débutant', 'Maîtrisant', 'Expert'],
      state: 'Active',
      contextes: null,
      environnements: null,
      missions: null,
      competencesByLevel: null,
    },
  ];
}

/** Jeu d'arêtes de corrélation de démonstration (version DRAFT). */
function demoEdges(): CorrelationEdgeView[] {
  return [
    {
      edgeId: 'edge-001',
      catalogVersionId: DEMO_DRAFT_VERSION_ID,
      profileFrom: 'PROF-DEV-001',
      profileTo: 'PROF-DATA-002',
      weight: 0.6,
      state: 'Active',
    },
  ];
}

/** Jeu de compétences de démonstration (2 états couverts : Active, Archived). */
function demoSkills(): SkillView[] {
  return [
    {
      skillCode: 'SK-PY-001',
      libelle: 'Python',
      description: '<p>Maîtrise du langage <strong>Python</strong> et de son écosystème.</p>',
      categorie: 'Langages',
      state: 'Active',
      createdAt: '2026-03-01T09:00:00.000Z',
      updatedAt: '2026-03-01T09:00:00.000Z',
      familles: ['Études et développement', 'Data'],
    },
    {
      skillCode: 'SK-COB-002',
      libelle: 'COBOL',
      description: 'Maintenance d’applications legacy COBOL.',
      categorie: 'Langages',
      state: 'Archived',
      createdAt: '2026-01-15T09:00:00.000Z',
      updatedAt: '2026-01-15T09:00:00.000Z',
      familles: ['Études et développement'],
    },
  ];
}

/** Génère un code de profil déterministe à la création (inv.1 simulé). */
function allocateProfileCode(seq: number): string {
  return `PROF-NEW-${String(seq).padStart(3, '0')}`;
}

/** Double in-memory du client d'écriture admin catalog. Déterministe, sans I/O. */
export class StubCatalogAdminEditionClient implements CatalogAdminEditionClient {
  private readonly profiles: ProfileView[];
  private readonly edges: CorrelationEdgeView[];
  private readonly skills: SkillView[];
  /** Usage campagne EN COURS par profil (avertissement d'archivage §2.4). Défaut vide (aucun impact). */
  private readonly profileCampaignUsage: readonly ProfileCampaignUsageSeed[];
  /** Usage campagne EN COURS par compétence (avertissement d'archivage R15-CAT-03). Défaut vide. */
  private readonly skillCampaignUsage: readonly SkillCampaignUsageSeed[];
  private profileSeq = 0;
  private edgeSeq = 0;
  /** Tentatives d'upload en attente de décision (dry-run validé, non encore confirmé). */
  private readonly attempts = new Map<string, UploadDryRunPreview>();
  private attemptSeq = 0;
  private versionSeq = 0;

  public constructor(options?: {
    readonly profiles?: ProfileView[];
    readonly edges?: CorrelationEdgeView[];
    readonly skills?: SkillView[];
    /** Force un dry-run avec anomalie bloquante (inv.7) au prochain upload. */
    readonly nextUploadBlocking?: boolean;
    /** Usage campagne EN COURS par profil (avertissement d'archivage §2.4). Défaut : aucun. */
    readonly profileCampaignUsage?: readonly ProfileCampaignUsageSeed[];
    /** Usage campagne EN COURS par compétence (avertissement d'archivage R15-CAT-03). Défaut : aucun. */
    readonly skillCampaignUsage?: readonly SkillCampaignUsageSeed[];
  }) {
    this.profiles = (options?.profiles ?? demoProfiles()).map((p) => ({ ...p }));
    this.edges = (options?.edges ?? demoEdges()).map((e) => ({ ...e }));
    this.skills = (options?.skills ?? demoSkills()).map((s) => ({ ...s }));
    this.nextUploadBlocking = options?.nextUploadBlocking ?? false;
    this.profileCampaignUsage = (options?.profileCampaignUsage ?? []).map((u) => ({
      profileCode: u.profileCode,
      entry: { ...u.entry },
    }));
    this.skillCampaignUsage = (options?.skillCampaignUsage ?? []).map((u) => ({
      skillCode: u.skillCode,
      entry: { ...u.entry },
    }));
  }

  /** Si vrai, le prochain dry-run renvoie une anomalie bloquante (test inv.7). */
  private nextUploadBlocking: boolean;

  // --- A-09 profil ---

  public listProfiles(
    catalogVersionId: string,
  ): Promise<readonly ProfileView[]> {
    return Promise.resolve(
      this.profiles
        .filter((p) => p.catalogVersionId === catalogVersionId)
        .map((p) => ({ ...p })),
    );
  }

  public upsertProfile(
    catalogVersionId: string,
    input: EditProfileInputDto,
  ): Promise<ProfileView> {
    // inv.2 (MIROIR) : édition réservée à la version DRAFT de démonstration.
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Édition refusée : seule une version au statut Brouillon (DRAFT) est éditable.',
        ),
      );
    }
    if (input.libelle.trim().length === 0) {
      return Promise.reject(
        new CatalogApiError('Le libellé du profil est obligatoire.'),
      );
    }
    if (input.profileCode === undefined) {
      this.profileSeq += 1;
      const created: ProfileView = {
        profileCode: allocateProfileCode(this.profileSeq),
        catalogVersionId,
        libelle: input.libelle,
        famille: input.famille,
        sousFamille: input.sousFamille,
        description: input.description,
        availableLevels: [...input.availableLevels],
        state: 'Active',
        // Contextes/environnements (DBU §2.4) : absent/null ⇒ null (non renseigné).
        contextes: input.contextes ?? null,
        environnements: input.environnements ?? null,
        // Missions (l.950/952) + compétences par niveau (F0-b) : absent/null ⇒ null.
        missions: input.missions ?? null,
        competencesByLevel: input.competencesByLevel ?? null,
      };
      this.profiles.unshift(created);
      return Promise.resolve({ ...created });
    }
    const existing = this.profiles.find(
      (p) =>
        p.profileCode === input.profileCode &&
        p.catalogVersionId === catalogVersionId,
    );
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil introuvable : « ${input.profileCode} ».`),
      );
    }
    const updated: ProfileView = {
      ...existing,
      libelle: input.libelle,
      famille: input.famille,
      sousFamille: input.sousFamille,
      description: input.description,
      availableLevels: [...input.availableLevels],
      // Contextes/environnements (DBU §2.4) : string ⇒ écrit ; null ⇒ effacé ; absent ⇒ inchangé.
      ...(input.contextes !== undefined ? { contextes: input.contextes } : {}),
      ...(input.environnements !== undefined
        ? { environnements: input.environnements }
        : {}),
      // Missions (l.950/952) + compétences par niveau (F0-b) : même sémantique partielle.
      ...(input.missions !== undefined ? { missions: input.missions } : {}),
      ...(input.competencesByLevel !== undefined
        ? { competencesByLevel: input.competencesByLevel }
        : {}),
    };
    const idx = this.profiles.indexOf(existing);
    this.profiles[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  /**
   * ARCHIVE un Profile (A-CATALOG §2.4, MIROIR des gardes serveur R1/R2/R3) : bascule
   * `state=Archived` ET cascade ses arêtes de corrélation impliquées (`state=Archived`), dans
   * l'ordre du service. Gardes reproduites À L'IDENTIQUE des codes 409 STRUCTURÉS de l'API :
   *  • R1 : version ≠ DRAFT ⇒ `CATALOG_VERSION_WRONG_STATE`.
   *  • R2 : version/profil introuvable ⇒ erreur sans code (parité 404, message serveur).
   *  • R3 : profil déjà archivé ⇒ `PROFILE_ALREADY_ARCHIVED` (idempotence).
   */
  public archiveProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    // R1 (clé de voûte) : archivage réservé à la version DRAFT de démonstration.
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Archivage refusé : seule une version au statut Brouillon (DRAFT) est éditable.',
          CATALOG_VERSION_WRONG_STATE,
        ),
      );
    }
    const existing = this.profiles.find(
      (p) =>
        p.profileCode === profileCode &&
        p.catalogVersionId === catalogVersionId,
    );
    // R2 : introuvable ⇒ pas de code (parité NotFoundException remontée telle quelle).
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil introuvable : « ${profileCode} ».`),
      );
    }
    // R3 (idempotence) : déjà archivé ⇒ code dédié.
    if (existing.state === 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          `Profil déjà archivé : « ${profileCode} ».`,
          PROFILE_ALREADY_ARCHIVED,
        ),
      );
    }
    const updated: ProfileView = { ...existing, state: 'Archived' };
    const idx = this.profiles.indexOf(existing);
    this.profiles[idx] = updated;
    // Cascade : les arêtes impliquant ce profil basculent en Archived (transaction service).
    for (let i = 0; i < this.edges.length; i += 1) {
      const edge = this.edges[i];
      if (
        edge !== undefined &&
        edge.catalogVersionId === catalogVersionId &&
        (edge.profileFrom === profileCode || edge.profileTo === profileCode)
      ) {
        this.edges[i] = { ...edge, state: 'Archived' };
      }
    }
    return Promise.resolve({ ...updated });
  }

  /**
   * RESTAURE un Profile archivé (DBU §2.4 l.960, MIROIR des gardes serveur). Gardes 409 STRUCTURÉS :
   *  • R1 : version ≠ DRAFT ⇒ `CATALOG_VERSION_WRONG_STATE`.
   *  • R2 : introuvable ⇒ erreur sans code (parité 404).
   *  • R3 (inverse) : profil déjà Actif ⇒ `PROFILE_NOT_ARCHIVED`.
   * NE réactive PAS les arêtes (l.964 : les corrélations archivées restent Archived).
   */
  public restoreProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Restauration refusée : seule une version au statut Brouillon (DRAFT) est éditable.',
          CATALOG_VERSION_WRONG_STATE,
        ),
      );
    }
    const existing = this.profiles.find(
      (p) => p.profileCode === profileCode && p.catalogVersionId === catalogVersionId,
    );
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil introuvable : « ${profileCode} ».`),
      );
    }
    if (existing.state !== 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          `Profil non archivé : « ${profileCode} ».`,
          PROFILE_NOT_ARCHIVED,
        ),
      );
    }
    const updated: ProfileView = { ...existing, state: 'Active' };
    this.profiles[this.profiles.indexOf(existing)] = updated;
    // Aucune réactivation d'arête (parité DEC-8 CODE-ONLY).
    return Promise.resolve({ ...updated });
  }

  /**
   * CLONE un Profile (DBU §2.4 l.950, MIROIR). R1 version DRAFT + R2 source existe ; alloue un code NEUF
   * (inv.1), copie le contenu (libellé « (copie) »), état Active, AUCUNE arête copiée.
   */
  public cloneProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Clonage refusé : seule une version au statut Brouillon (DRAFT) est éditable.',
          CATALOG_VERSION_WRONG_STATE,
        ),
      );
    }
    const source = this.profiles.find(
      (p) => p.profileCode === profileCode && p.catalogVersionId === catalogVersionId,
    );
    if (source === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil source introuvable : « ${profileCode} ».`),
      );
    }
    this.profileSeq += 1;
    const clone: ProfileView = {
      ...source,
      profileCode: allocateProfileCode(this.profileSeq),
      libelle: `${source.libelle} (copie)`,
      state: 'Active',
    };
    this.profiles.unshift(clone);
    return Promise.resolve({ ...clone });
  }

  /**
   * FUSIONNE la source dans la cible (R15-CAT-01, DBU §2.4 l.866, MIROIR des gardes serveur) : transfère
   * les corrélations ACTIVES source→cible (self-loop source↔cible → Archived) puis archive la source.
   * Gardes reproduites À L'IDENTIQUE des codes STRUCTURÉS de l'API :
   *  • R1 : version ≠ DRAFT ⇒ `CATALOG_VERSION_WRONG_STATE`.
   *  • R2 : source/cible introuvable ⇒ erreur sans code (parité 404, message serveur).
   *  • same ⇒ `PROFILE_MERGE_SAME_PROFILE` ; confirmation ⇒ `PROFILE_MERGE_CONFIRMATION_MISMATCH` ;
   *    opérande archivé ⇒ `PROFILE_MERGE_OPERAND_ARCHIVED`.
   * Renvoie la vue de la CIBLE (survivant).
   */
  public mergeProfiles(
    catalogVersionId: string,
    sourceProfileCode: string,
    targetProfileCode: string,
    confirmationCode: string,
  ): Promise<ProfileView> {
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Fusion refusée : seule une version au statut Brouillon (DRAFT) est éditable.',
          CATALOG_VERSION_WRONG_STATE,
        ),
      );
    }
    const source = this.profiles.find(
      (p) => p.profileCode === sourceProfileCode && p.catalogVersionId === catalogVersionId,
    );
    if (source === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil source introuvable : « ${sourceProfileCode} ».`),
      );
    }
    const target = this.profiles.find(
      (p) => p.profileCode === targetProfileCode && p.catalogVersionId === catalogVersionId,
    );
    if (target === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil cible introuvable : « ${targetProfileCode} ».`),
      );
    }
    if (sourceProfileCode === targetProfileCode) {
      return Promise.reject(
        new CatalogApiError(
          `Fusion impossible : la source et la cible sont le même profil « ${sourceProfileCode} ».`,
          PROFILE_MERGE_SAME_PROFILE,
        ),
      );
    }
    if (confirmationCode !== sourceProfileCode) {
      return Promise.reject(
        new CatalogApiError(
          `Confirmation invalide : resaisissez le code du profil source « ${sourceProfileCode} ».`,
          PROFILE_MERGE_CONFIRMATION_MISMATCH,
        ),
      );
    }
    if (source.state === 'Archived' || target.state === 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          'Fusion impossible : les deux profils doivent être actifs.',
          PROFILE_MERGE_OPERAND_ARCHIVED,
        ),
      );
    }
    // Transfert des arêtes ACTIVES source→cible ; self-loop source↔cible ⇒ Archived (parité service).
    for (let i = 0; i < this.edges.length; i += 1) {
      const edge = this.edges[i];
      if (
        edge === undefined ||
        edge.catalogVersionId !== catalogVersionId ||
        edge.state !== 'Active' ||
        (edge.profileFrom !== sourceProfileCode && edge.profileTo !== sourceProfileCode)
      ) {
        continue;
      }
      const newFrom = edge.profileFrom === sourceProfileCode ? targetProfileCode : edge.profileFrom;
      const newTo = edge.profileTo === sourceProfileCode ? targetProfileCode : edge.profileTo;
      this.edges[i] =
        newFrom === newTo
          ? { ...edge, state: 'Archived' }
          : { ...edge, profileFrom: newFrom, profileTo: newTo };
    }
    // Archivage de la source.
    const archivedSource: ProfileView = { ...source, state: 'Archived' };
    this.profiles[this.profiles.indexOf(source)] = archivedSource;
    // Renvoie la CIBLE (survivant).
    return Promise.resolve({ ...target });
  }

  /** Suggère un code candidat AFFICHABLE (DBU §2.4 l.952). Aucune réservation (l'allocation reste au PUT). */
  public suggestProfileCode(
    _catalogVersionId: string,
  ): Promise<{ readonly profileCode: string }> {
    return Promise.resolve({ profileCode: allocateProfileCode(this.profileSeq + 1) });
  }

  /** Lit un Profile (visualiser, DBU §2.4 l.950). 404 (message serveur) si absent de la version. */
  public getProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    const found = this.profiles.find(
      (p) => p.profileCode === profileCode && p.catalogVersionId === catalogVersionId,
    );
    if (found === undefined) {
      return Promise.reject(
        new CatalogApiError(`Profil introuvable : « ${profileCode} ».`),
      );
    }
    return Promise.resolve({ ...found });
  }

  // --- Matrice de corrélation ---

  public listEdges(
    catalogVersionId: string,
  ): Promise<readonly CorrelationEdgeView[]> {
    return Promise.resolve(
      this.edges
        .filter((e) => e.catalogVersionId === catalogVersionId)
        .map((e) => ({ ...e })),
    );
  }

  /**
   * Campagnes EN COURS référençant le profil (avertissement d'archivage §2.4, A0R-10). Le seed représente
   * la réponse API DÉJÀ filtrée « en cours » par le carrier back : le stub la restitue telle quelle pour le
   * profileCode ciblé (`[]` si aucun seed). LECTURE SEULE, INFORMATIONNEL (jamais un blocage).
   */
  public listProfileCampaignUsage(
    _catalogVersionId: string,
    profileCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]> {
    return Promise.resolve(
      this.profileCampaignUsage
        .filter((u) => u.profileCode === profileCode)
        .map((u) => ({ ...u.entry })),
    );
  }

  /**
   * Campagnes EN COURS où la compétence est PROPOSABLE (avertissement d'archivage R15-CAT-03, DEC-27). Le
   * seed représente la réponse API DÉJÀ résolue « proposable » + filtrée « en cours » par le carrier back :
   * le stub la restitue telle quelle pour le skillCode ciblé (`[]` si aucun seed). LECTURE SEULE,
   * INFORMATIONNEL (jamais un blocage).
   */
  public listSkillCampaignUsage(
    skillCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]> {
    return Promise.resolve(
      this.skillCampaignUsage
        .filter((u) => u.skillCode === skillCode)
        .map((u) => ({ ...u.entry })),
    );
  }

  public upsertEdge(
    catalogVersionId: string,
    input: EditCorrelationEdgeInputDto,
  ): Promise<CorrelationEdgeView> {
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Édition refusée : seule une version au statut Brouillon (DRAFT) est éditable.',
        ),
      );
    }
    // Poids ∈ [0,1] (MIROIR de la garde service ; autorité = API).
    if (!Number.isFinite(input.weight) || input.weight < 0 || input.weight > 1) {
      return Promise.reject(
        new CatalogApiError('Poids invalide : il doit être compris entre 0 et 1.'),
      );
    }
    if (input.edgeId === undefined) {
      this.edgeSeq += 1;
      const created: CorrelationEdgeView = {
        edgeId: `edge-new-${String(this.edgeSeq).padStart(3, '0')}`,
        catalogVersionId,
        profileFrom: input.profileFrom,
        profileTo: input.profileTo,
        weight: input.weight,
        state: 'Active',
      };
      this.edges.unshift(created);
      return Promise.resolve({ ...created });
    }
    const existing = this.edges.find(
      (e) => e.edgeId === input.edgeId && e.catalogVersionId === catalogVersionId,
    );
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Arête introuvable : « ${input.edgeId} ».`),
      );
    }
    const updated: CorrelationEdgeView = {
      ...existing,
      profileFrom: input.profileFrom,
      profileTo: input.profileTo,
      weight: input.weight,
    };
    const idx = this.edges.indexOf(existing);
    this.edges[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  // --- A-08 promotion DRAFT→PUBLISHED (DBU §2.4) ---

  /**
   * Promotion DRAFT→PUBLISHED (MIROIR de la garde de transition). Seule la version DRAFT de
   * démonstration (`DEMO_DRAFT_VERSION_ID`) est promouvable ; toute autre id ⇒ refus (parité 409
   * serveur : état ≠ DRAFT). Au succès, rend une vue PUBLISHED déterministe.
   */
  public publishVersion(catalogVersionId: string): Promise<CatalogVersionDto> {
    if (catalogVersionId !== DEMO_DRAFT_VERSION_ID) {
      return Promise.reject(
        new CatalogApiError(
          'Publication refusée : seule une version au statut Brouillon (DRAFT) peut être publiée.',
        ),
      );
    }
    return Promise.resolve({
      catalogVersionId,
      versionNumber: '2026.03',
      state: 'PUBLISHED',
      createdAt: '2026-03-01T09:00:00.000Z',
      publishedAt: '2026-03-15T10:00:00.000Z',
      archivedAt: null,
    });
  }

  // --- A-25 compétences ---

  public listSkills(): Promise<readonly SkillView[]> {
    return Promise.resolve(this.skills.map((s) => ({ ...s })));
  }

  /**
   * REM-A #1 : RÉFÉRENTIEL des familles associables — DISTINCT `Profile.famille` (toutes versions/états),
   * trié. Parité API : source de vérité du SÉLECTEUR contraint d'association Compétence↔Familles.
   */
  public listAssociableFamilies(): Promise<readonly string[]> {
    const familles = new Set(this.profiles.map((p) => p.famille));
    return Promise.resolve([...familles].sort((a, b) => a.localeCompare(b, 'fr')));
  }

  /**
   * REM-A #3 : MATRICE d'associations Compétences×Familles en CSV (parité API). Lignes = compétences ;
   * colonnes = familles DISTINCTES associées (triées fr) ; cellule = « X » si associée. RFC-4180 minimal
   * (le durcissement anti-injection réel est côté API ; le stub n'a pas de valeurs hostiles).
   */
  public exportSkillsMatrixCsv(): Promise<string> {
    const familles = [
      ...new Set(this.skills.flatMap((s) => s.familles)),
    ].sort((a, b) => a.localeCompare(b, 'fr'));
    const header = ['skill_code', 'libelle', ...familles];
    const rows = this.skills.map((s) => {
      const associated = new Set(s.familles);
      return [
        s.skillCode,
        s.libelle,
        ...familles.map((f) => (associated.has(f) ? 'X' : '')),
      ];
    });
    const lines = [header, ...rows].map((cells) => cells.join(','));
    return Promise.resolve(lines.join('\r\n'));
  }

  public createSkill(input: CreateSkillInputDto): Promise<SkillView> {
    if (input.skillCode.trim().length === 0) {
      return Promise.reject(
        new CatalogApiError('Le code de la compétence est obligatoire.'),
      );
    }
    // inv.1 (MIROIR) : unicité perpétuelle du skillCode.
    if (this.skills.some((s) => s.skillCode === input.skillCode)) {
      return Promise.reject(
        new CatalogApiError(`Compétence déjà référencée : « ${input.skillCode} ».`),
      );
    }
    const createdAt = new Date(
      Date.UTC(2026, 5, 1, 0, 0, this.skills.length),
    ).toISOString();
    const created: SkillView = {
      skillCode: input.skillCode,
      libelle: input.libelle,
      description: input.description,
      categorie: input.categorie,
      state: 'Active',
      createdAt,
      // MIROIR @updatedAt : à la création, updatedAt = createdAt.
      updatedAt: createdAt,
      familles: [...input.familles],
    };
    this.skills.unshift(created);
    return Promise.resolve({ ...created });
  }

  public updateSkill(
    skillCode: string,
    input: UpdateSkillInputDto,
  ): Promise<SkillView> {
    const existing = this.skills.find((s) => s.skillCode === skillCode);
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Compétence introuvable : « ${skillCode} ».`),
      );
    }
    // GARDE #4 (MIROIR, DBU §2.10 l.2222) : l'édition est réservée aux compétences ACTIVES.
    if (existing.state === 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          `Compétence « ${skillCode} » non modifiable : restaurez-la avant de l'éditer.`,
          SKILL_ARCHIVED_NOT_EDITABLE,
        ),
      );
    }
    // `state` VOLONTAIREMENT IGNORÉ (MIROIR du service) : l'archivage/restauration passe par les
    // primitives DÉDIÉES (archiveSkill / restoreSkill), jamais une bascule d'état silencieuse (bug #16).
    const updated: SkillView = {
      ...existing,
      ...(input.libelle !== undefined ? { libelle: input.libelle } : {}),
      ...(input.description !== undefined
        ? { description: input.description }
        : {}),
      ...(input.categorie !== undefined ? { categorie: input.categorie } : {}),
      // MIROIR @updatedAt : l'édition de contenu fait AVANCER la date de dernière modification (#24).
      updatedAt: bumpIso(existing.updatedAt),
    };
    const idx = this.skills.indexOf(existing);
    this.skills[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  public setSkillFamilies(
    skillCode: string,
    familles: readonly string[],
  ): Promise<SkillView> {
    const existing = this.skills.find((s) => s.skillCode === skillCode);
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Compétence introuvable : « ${skillCode} ».`),
      );
    }
    // GARDE #4 (MIROIR) : association/dissociation réservée aux compétences ACTIVES.
    if (existing.state === 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          `Compétence « ${skillCode} » non modifiable : restaurez-la avant de l'éditer.`,
          SKILL_ARCHIVED_NOT_EDITABLE,
        ),
      );
    }
    const updated: SkillView = { ...existing, familles: [...familles] };
    const idx = this.skills.indexOf(existing);
    this.skills[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  public archiveSkill(skillCode: string): Promise<SkillView> {
    const existing = this.skills.find((s) => s.skillCode === skillCode);
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Compétence introuvable : « ${skillCode} ».`),
      );
    }
    // inv.4 (MIROIR) : archivage = state Archived, associations PRÉSERVÉES (no-DELETE). L'UPDATE d'état
    // fait AVANCER updatedAt (MIROIR @updatedAt).
    const updated: SkillView = {
      ...existing,
      state: 'Archived',
      updatedAt: bumpIso(existing.updatedAt),
    };
    const idx = this.skills.indexOf(existing);
    this.skills[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  /**
   * RESTAURE une compétence archivée (Archivé → Actif, DBU §2.10 l.2256 ; #16). Primitive DÉDIÉE
   * (MIROIR du service) : la restauration N'est PLUS un `updateSkill({state})` (NO-OP côté service).
   * Garde inverse : refuse une compétence non archivée (409 `SKILL_NOT_ARCHIVED`).
   */
  public restoreSkill(skillCode: string): Promise<SkillView> {
    const existing = this.skills.find((s) => s.skillCode === skillCode);
    if (existing === undefined) {
      return Promise.reject(
        new CatalogApiError(`Compétence introuvable : « ${skillCode} ».`),
      );
    }
    if (existing.state !== 'Archived') {
      return Promise.reject(
        new CatalogApiError(
          `Compétence non archivée : « ${skillCode} ».`,
          SKILL_NOT_ARCHIVED,
        ),
      );
    }
    // #16 (MIROIR) : bascule Archivé → Actif, associations PRÉSERVÉES. L'UPDATE d'état fait AVANCER
    // updatedAt (MIROIR @updatedAt).
    const updated: SkillView = {
      ...existing,
      state: 'Active',
      updatedAt: bumpIso(existing.updatedAt),
    };
    const idx = this.skills.indexOf(existing);
    this.skills[idx] = updated;
    return Promise.resolve({ ...updated });
  }

  // --- A-10 upload (dry-run) ---

  public uploadDryRun(file: File): Promise<UploadDryRunPreview> {
    this.attemptSeq += 1;
    const attemptId = `att-${String(this.attemptSeq).padStart(3, '0')}`;
    const blocking = this.nextUploadBlocking;
    this.nextUploadBlocking = false;

    const preview: UploadDryRunPreview = blocking
      ? {
          attemptId,
          sourceFilename: file.name,
          parsingStatus: 'ParsingOk',
          validationStatus: 'Failed',
          anomalies: [
            {
              code: 'PROFILE_CODE_DUPLICATE',
              message: 'Code de profil dupliqué dans le classeur.',
              row: 12,
              severity: 'blocking',
            },
            {
              code: 'SKILL_FAMILY_UNKNOWN',
              message: 'Famille de profil inconnue référencée.',
              row: 27,
              severity: 'warning',
            },
          ],
          changeSummary: {
            profilesAdded: 3,
            profilesModified: 1,
            profilesRemoved: 0,
            skillsAdded: 2,
            skillsModified: 0,
            skillsRemoved: 0,
          },
          hasBlockingAnomalies: true,
        }
      : {
          attemptId,
          sourceFilename: file.name,
          parsingStatus: 'ParsingOk',
          validationStatus: 'Passed',
          anomalies: [
            {
              code: 'DESCRIPTION_EMPTY',
              message: 'Description de profil vide (toléré).',
              row: 5,
              severity: 'warning',
            },
          ],
          changeSummary: {
            profilesAdded: 4,
            profilesModified: 2,
            profilesRemoved: 1,
            skillsAdded: 3,
            skillsModified: 1,
            skillsRemoved: 0,
          },
          hasBlockingAnomalies: false,
        };

    this.attempts.set(attemptId, preview);
    return Promise.resolve(preview);
  }

  public confirmUpload(
    attemptId: string,
    versionNumber: string,
  ): Promise<ConfirmUploadResult> {
    const attempt = this.attempts.get(attemptId);
    if (attempt === undefined) {
      return Promise.reject(
        new CatalogApiError(`Tentative d'upload introuvable : « ${attemptId} ».`),
      );
    }
    if (versionNumber.trim().length === 0) {
      return Promise.reject(
        new CatalogApiError('Le numéro de version est obligatoire pour confirmer.'),
      );
    }
    // inv.7 (MIROIR) : promotion REFUSÉE si anomalie bloquante.
    if (attempt.hasBlockingAnomalies) {
      return Promise.reject(
        new CatalogApiError(
          'Promotion refusée : la tentative comporte au moins une anomalie bloquante.',
        ),
      );
    }
    this.attempts.delete(attemptId);
    this.versionSeq += 1;
    return Promise.resolve({
      attemptId,
      producedVersionId: `cat-vnew-${String(this.versionSeq).padStart(3, '0')}`,
    });
  }

  public cancelUpload(
    attemptId: string,
  ): Promise<{ readonly attemptId: string; readonly outcome: 'Cancelled' }> {
    this.attempts.delete(attemptId);
    return Promise.resolve({ attemptId, outcome: 'Cancelled' });
  }
}

/** Instance par défaut injectée par le provider d'édition catalog (stub-first). */
export const defaultCatalogAdminEditionClient: CatalogAdminEditionClient =
  new StubCatalogAdminEditionClient();
